import { useCurrentAccount } from '@application/api/account/useCurrentAccount';
import { CACHE_KEY_NEWSLETTER_MODAL_PERSIST_DATE } from '@application/constants';
import type { Route } from '@application/types';
import { useModal, useNewsletter, useRouter } from '@application/utils';
import { NewsletterModal } from '@presentation/components';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';
import { EmailContactCreatedFromEnum } from '@gql/graphql';
import { useSocialShareModal } from '../SocialShareModalController';

export const NEWSLETTER_PERSIST_DAYS = 10;

const NEWSLETTER_HIDDEN_ROUTES: Route[] = [
  'joinBreeder',
  'joinBuyer',
  'joinVet',
  'joinEducator',
  'vet',
  'booking',
  'application',
  'country-selector' as Route,
];

export const NewsletterModalController = () => {
  const { show, modal, close } = useModal();
  const { modal: socialShareModal } = useSocialShareModal();
  const { newsletterProps } = useNewsletter({
    onSubmit: () => {
      close();
    },
    createdFrom: EmailContactCreatedFromEnum.NEWSLETTER,
  });
  const { currentAccountLoggedIn } = useCurrentAccount();
  const { infoFromCurrentPath } = useRouter();

  const shouldShow = useMemo((): boolean => {
    if (typeof window === 'undefined') return false; // Dont' show if server rendered
    if (currentAccountLoggedIn) return false; // Don't show if user is logged in
    if (NEWSLETTER_HIDDEN_ROUTES.includes(infoFromCurrentPath.route)) return false; // Don't show on excluded routes
    if (socialShareModal.open) return false;
    const timestamp = localStorage.getItem(CACHE_KEY_NEWSLETTER_MODAL_PERSIST_DATE);
    if (!timestamp) return true; // Show if no timestamp
    const date = dayjs(Number(timestamp));
    if (!date.isValid()) return true; // Show if timestamp is not valid
    return +dayjs() >= +date.add(NEWSLETTER_PERSIST_DAYS, 'days'); // Show if currentTime >= PERSIST_TIME + date
  }, [currentAccountLoggedIn, infoFromCurrentPath.route, socialShareModal.open]);

  const setPersistDate = useCallback(() => {
    localStorage.setItem(CACHE_KEY_NEWSLETTER_MODAL_PERSIST_DATE, new Date().getTime().toString());
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (shouldShow) {
      timeout = setTimeout(() => {
        show();
        setPersistDate();
      }, 1000 * 10);
    }

    return () => {
      // When shouldShow changes from false to true, clear timeout
      if (shouldShow) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow]);

  return <NewsletterModal modal={modal} newsletter={newsletterProps} />;
};
